import { useEffect } from 'react';
import dayjs from 'dayjs';
import { PaymentMethod, PaymentMethodStatus, PaymentPlanQueries } from '@frontend/api-payment-plans';
import { HttpError } from '@frontend/fetch';

export type UsePaymentMethodsJustifiProps = {
  autoSelectDefaultPM?: boolean;
  locationId?: string;
  personId?: string;
  selectedPM?: PaymentMethod;
  onSelectPaymentMethod?: (pm: PaymentMethod) => void;
  onError?: (message: string) => void;
};
export const usePaymentMethodsJustifi = ({
  autoSelectDefaultPM = true,
  locationId,
  personId,
  selectedPM,
  onSelectPaymentMethod,
  onError,
}: UsePaymentMethodsJustifiProps) => {
  const {
    data: paymentMethods,
    isLoading: isLoadingPaymentMethods,
    isRefetching: isRefetchingPaymentMethods,
  } = PaymentPlanQueries.useGetPaymentMethodsByPerson(
    {
      locationId: locationId || '',
      patientId: personId || '',
    },
    {
      onError: (error) => {
        if (error && error instanceof HttpError) {
          onError?.(error.message || 'Error in retrieving saved cards.');
        } else {
          console.error('Error retrieving saved cards:', error);
          onError?.('Unknown error in retrieving saved cards.');
        }
      },
      select: (paymentMethods) => {
        return paymentMethods.map((pm) => {
          const isCardExpired =
            pm.status === PaymentMethodStatus.PAYMENT_METHOD_STATUS_EXPIRED ||
            (pm.card && dayjs().isAfter(dayjs(`${pm.card.expYear}-${pm.card.expMonth}-01`)));
          return { ...pm, status: isCardExpired ? PaymentMethodStatus.PAYMENT_METHOD_STATUS_EXPIRED : pm.status };
        });
      },
    }
  );

  const getDefaultPaymentMethod = () => {
    if (selectedPM) {
      const found = paymentMethods?.find((pm) => pm.id === selectedPM.id);
      if (found) return found;
    }
    const filteredPaymentMethods = paymentMethods?.filter(
      (pm) =>
        !(
          pm.status &&
          [
            PaymentMethodStatus.PAYMENT_METHOD_STATUS_VERIFICATION_PENDING,
            PaymentMethodStatus.PAYMENT_METHOD_STATUS_EXPIRED,
          ].includes(pm.status)
        )
    );

    return filteredPaymentMethods?.find((pm) => pm.is_default) || filteredPaymentMethods?.[0];
  };

  useEffect(() => {
    if (!autoSelectDefaultPM || !onSelectPaymentMethod || selectedPM) return;

    const toSelect = getDefaultPaymentMethod();
    if (toSelect) {
      onSelectPaymentMethod(toSelect);
    }
  }, [selectedPM, paymentMethods]);

  return {
    paymentMethods: paymentMethods || [],
    isLoading: isLoadingPaymentMethods || isRefetchingPaymentMethods,
  };
};
